import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  CurrencyInput,
  Loader,
  Modal,
  ModalType,
  Option,
  Table,
  TableRow,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Parameter, ProductOperations } from 'src/front/api/apiInstance';

import {
  ProductOperationCriteriaData,
  ProductOperationResponseData,
  ReferenceDataType,
} from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';

import {
  formatAlphaNumeric,
  formatAmount,
  formatFreeText,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { ExecutedOperationSchema } from 'src/front/common/validations/executedOperation';

import Datepicker from 'src/front/components/Datepicker';

import FormModal from 'src/front/components/FormModal';

import {
  ButtonRow,
  Container,
  FormElement,
  FormGrid,
  FormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components/Markdown';
import { MODAL_VIEW, TABLE_WIDTHS } from 'src/front/constant';
import {
  useClearDispatch,
  useExecutedOperationState,
  useOperationSearchDispatch,
} from 'src/front/contexts/ExecutedOperationContext';
import { useUserInfoState } from 'src/front/contexts/SessionContext';

import { OperationInfo } from './ExecutedOperations';

type CriteriaFormatterMappingType = { [key: string]: (value: any) => string };
const CriteriaFormatterMapping: CriteriaFormatterMappingType = {
  CURRENCY: formatAmount,
  DATE: formatFreeText,
  ALPHANUMERIC: formatAlphaNumeric,
  NUMERIC: formatNumeric,
};

export interface ModalState {
  [key: string]: boolean;

  cancelConfirm: boolean;
  cancelledResult: boolean;
}

export interface OperationSearchFormData {
  beginDate: Date | null;
  endDate: Date | null;
  product: Option | null;
  productOperation: Option | null;
  referenceNumber: string;
  operationStatus: Option | null;
  amountFrom: string;
  amountTo: string;

  criteria1: string;
  criteria2: string;
  criteria3: string;
  criteria4: string;
  criteria5: string;
  criteria6: string;
}

const defaultValues = {
  beginDate: null,
  endDate: null,
  product: null,
  productOperation: null,
  referenceNumber: '',
  operationStatus: null,
  amountFrom: '',
  amountTo: '',

  criteria1: '',
  criteria2: '',
  criteria3: '',
  criteria4: '',
  criteria5: '',
  criteria6: '',
};

const operationStatusTableHeaders = [
  'general_date_label',
  'general_time_label',
  'general_status_label',
  'general_comment_label',
];

const getOperationTableHeadersDefinition = (hasCriterias: boolean): string[] => [
  'executedOperations_operationStatus_label',
  'general_date_label',
  'general_time_label',
  ...['executedOperations_product_label', 'executedOperations_productOperation_label'].filter(
    () => !hasCriterias
  ),
  'general_customerName_label',
  'executedOperations_referenceNumber_label',
  'executedOperations_instantFlag_label',
  ...[
    'executedOperations_criteria3_text',
    'executedOperations_criteria4_text',
    'executedOperations_criteria5_text',
    'executedOperations_criteria6_text',
  ].filter(() => !hasCriterias),
];

interface Props {
  onEditCardEntry: (info: OperationInfo) => Promise<void>;
}

const getColumnWidths = (headerLength: number): string[] => {
  return headerLength > 6
    ? ['8', '8', '6', '8', '10', '12', '8', '8', '8', '8', '8', '8']
    : ['12', '12', '15', '23', '19', '19'];
};

const OperationSearch: FC<Props> = ({ onEditCardEntry }) => {
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();

  const resolver = useYupResolver(ExecutedOperationSchema, { todayMaxControl: true });

  const dispatch = useOperationSearchDispatch();
  const clearOperationSearch = useClearDispatch();

  const { searchFormValues } = useExecutedOperationState();

  const [isShowResult, setShowResult] = useState<boolean>(
    !!searchFormValues?.selectedOperation?.id
  );
  const [operationList, setOperationList] = useState<ProductOperationResponseData[]>([]);
  const [operationTableItems, setOperationTableItems] = useState<TableRow[]>([]);
  const [operationTableHeaders, setOperationTableHeaders] = useState<string[]>([]);
  const [productOperations, setProductOperations] = useState<Option[]>([]);
  const [page, setPage] = useState<number>(searchFormValues?.operationPageNumber || 1);
  const [selectedOperationPage, setSelectedOperationPage] = useState<number>(
    searchFormValues?.operationPageNumber || 1
  );
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [isLoadingTable, setLoadingTable] = useState<boolean>(false);
  const [criteriaList, setCriteriaList] = useState<ProductOperationCriteriaData[]>([]);

  const [isLoadingAction, setLoadingAction] = useState<boolean>(false);
  const [productOperationStatusList, setProductOperationStatusList] = useState<TableRow[]>();

  const [showModal, setShowModal] = useState<ModalState>({
    cancelConfirm: false,
    cancelledResult: false,
  });
  const [selectedOperation, setSelectedOperation] = useState<TableRow | undefined>(
    searchFormValues?.selectedOperation
  );

  const products = usePRM(ReferenceDataType.PRODUCTS);
  const productStatuses = usePRM(ReferenceDataType.PRODUCTOPERATIONSTATUS);
  const defaultProcessDate = processDate || null;

  const {
    watch,
    register,
    errors,
    setValue,
    handleSubmit,
    control,
    getValues,
    formState,
  } = useForm<OperationSearchFormData>({
    defaultValues: {
      ...defaultValues,
      ...searchFormValues,
      beginDate: searchFormValues?.beginDate || defaultProcessDate,
      endDate: searchFormValues?.endDate || defaultProcessDate,
    },
    resolver,
  });

  useEffect(() => {
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
    register({ name: 'referenceNumber' });
    register({ name: 'product' });
    register({ name: 'productOperation' });
    register({ name: 'criteria1' });
    register({ name: 'criteria2' });
    register({ name: 'criteria3' });
    register({ name: 'criteria4' });
    register({ name: 'criteria5' });
    register({ name: 'criteria6' });
  }, [register]);

  const handleProductChange = useCallback(
    (isSessionLoad = true) => async (product: Option | null): Promise<void> => {
      setValue('product', product);
      if (!isSessionLoad) {
        setValue('productOperation', null);
      }
      if (product) {
        const productOperationResponse = await Parameter.getReferenceData({
          referenceDataType: ReferenceDataType.PRODUCTOPERATIONS,
          additionalCode: product?.value,
        });

        if (productOperationResponse.data.result === 'OK') {
          setProductOperations(
            productOperationResponse.data.parameterDataList?.map(optionFormat) || []
          );
        } else {
          setProductOperations([]);
        }
      } else {
        setProductOperations([]);
      }
    },
    [setValue]
  );

  const loadExecutedOperations = useCallback(
    async (pageNumber = 1, forceRefresh = false): Promise<void> => {
      setLoadingTable(true);
      setShowResult(false);

      try {
        const {
          amountFrom,
          amountTo,
          endDate,
          operationStatus,
          product,
          productOperation,
          referenceNumber,
          beginDate,
          criteria3,
          criteria4,
          criteria5,
          criteria6,
        } = getValues();
        const { data } = await ProductOperations.getProductOperations({
          beginDate: beginDate ? formatDate(beginDate) : '',
          endDate: endDate ? formatDate(endDate) : '',
          productCode: product?.value,
          amountLowerBound: Number(formatNumeric(amountFrom)),
          amountUpperBound: Number(formatNumeric(amountTo)),
          productOperationCode: productOperation?.value,
          productOperationStatusCode: operationStatus?.value,
          referenceNumber,
          rowCountPerPage: 5,
          pageNumber: pageNumber || page,
          refresh: !pageNumber || forceRefresh,
          criteria3,
          criteria4,
          criteria5,
          criteria6,
        });

        if (data.result === 'OK') {
          const hasCriteria = !!productOperation && !!criteriaList.length;

          setHasNextPage(data.hasNextPage);
          setPage(pageNumber || 1);
          setOperationList(data.productOperationList);
          setOperationTableHeaders(getOperationTableHeadersDefinition(hasCriteria));
          setOperationTableItems(
            data.productOperationList.map((operation) => ({
              id: operation.referenceNumber || '',
              columns: [
                operation.productOperationStatus,
                operation.processDate,
                operation.criteria6 || '',
                ...[operation.productName, operation.productOperationName || ''].filter(
                  () => !hasCriteria
                ),
                operation.clientName || '',
                operation.referenceNumber || '',
                ...[
                  operation.criteria1 || '',
                  operation.criteria2 || '',
                  operation.criteria3 || '',
                  operation.criteria4 || '',
                ].filter(() => !hasCriteria),
                operation.criteria5 || '',
              ],
              customerNumber: operation.customerNumber,
            }))
          );
        }
      } catch (err) {
        // todo(ctasci): it's handled with error boundary
      }

      setShowResult(true);
      setLoadingTable(false);
    },
    [criteriaList.length, getValues, page]
  );

  useEffect(() => {
    if (
      searchFormValues?.product &&
      searchFormValues.operationPageNumber &&
      productOperations.length === 0 &&
      operationList.length === 0
    ) {
      handleProductChange(true)(searchFormValues?.product);
      loadExecutedOperations(searchFormValues.operationPageNumber, true);
    }
  }, [
    handleProductChange,
    loadExecutedOperations,
    operationList.length,
    productOperations.length,
    searchFormValues,
  ]);

  const handleSelectOperation = useCallback(
    async (customer: TableRow | undefined): Promise<void> => {
      setSelectedOperation(customer);
      setSelectedOperationPage(page);
    },
    [page]
  );

  const handleProductOperationChange = useCallback(
    async (productOperation: Option | null): Promise<void> => {
      setValue('productOperation', productOperation);

      if (productOperation) {
        const product = getValues('product');

        const { data } = await ProductOperations.getProductCriterias({
          productCode: product?.value || '',
          productOperationCode: productOperation?.value || '',
        });

        if (data.result === 'OK') {
          setCriteriaList(data.criteriaList);
        } else {
          setCriteriaList([]);
        }
      } else {
        setCriteriaList([]);
      }
    },
    [getValues, setValue]
  );

  const handleTextChange = (fieldName: string) => (text: string) =>
    setValue(fieldName, text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const handleDateChange = (name: string) => (date: Date): void =>
    setValue(name, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

  const clearPreviousValues = (): void => {
    clearOperationSearch();
    setOperationList([]);
    setSelectedOperation(undefined);
    setSelectedOperationPage(1);
  };

  const onSubmit = async (): Promise<void> => {
    clearPreviousValues();
    setShowModal((prevState) => ({
      ...prevState,
      result: true,
    }));
    selectedOperation && setSelectedOperation(undefined);

    loadExecutedOperations(1, true);
  };

  const handleViewStatus = async (): Promise<void> => {
    const { data } = await ProductOperations.getProductOperationsStatus({
      referenceNumber: selectedOperation?.id || '',
    });

    if (data.result === 'OK') {
      setProductOperationStatusList(
        data.productOperationStatusList.map((status) => ({
          id: status.explanation,
          columns: [
            status.processDate,
            status.processTime,
            status.productOperationStatus || '',
            status.explanation,
          ],
        }))
      );
    } else {
      setProductOperationStatusList(undefined);
    }
  };

  const handleOperationStatusModalClose = (): void => {
    setProductOperationStatusList(undefined);
  };

  const handleCancelSubmit = async (): Promise<void> => {
    setLoadingAction(true);
    try {
      const { data } = await ProductOperations.cancelProductOperation({
        referenceNumber: selectedOperation?.id || '',
      });

      if (data.result === 'OK') {
        setShowModal((prevState) => ({
          ...prevState,
          cancelConfirm: false,
          cancelledResult: true,
        }));
        clearPreviousValues();
        loadExecutedOperations(page, true);
      }
    } catch {
      // todo(ctasci): it's handled with error boundary
    }

    setLoadingAction(false);
  };

  const toggleModal = (key: string) => (): void => {
    setShowModal((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const handleEdit = async (): Promise<void> => {
    setLoadingAction(true);
    if (originalOperationData && selectedOperation) {
      try {
        dispatch({
          searchFormValues: {
            ...getValues(),
            selectedOperation,
            operationPageNumber: selectedOperationPage,
          },
        });
        const customerNameIndex = operationTableHeaders.findIndex(
          (val) => val === 'general_customerName_label'
        );
        await onEditCardEntry({
          referenceNumber: originalOperationData.referenceNumber,
          menuId: originalOperationData.menuID,
          customerName: selectedOperation.columns[customerNameIndex],
        });
      } catch {
        setLoadingAction(false);
      }
    }
  };

  const originalOperationData = useMemo(
    () =>
      selectedOperation
        ? operationList.find((operation) => operation.referenceNumber === selectedOperation.id)
        : null,
    [operationList, selectedOperation]
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="executedOperations_title" />
      </Title>
      <Separator />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Datepicker
              errorMessage={t(errors?.beginDate?.message as string)}
              inputLabel={`${t('executedOperations_startDate_label')} *`}
              onChange={handleDateChange('beginDate')}
              value={watch('beginDate') as Date}
            />
          </FormElement>

          <FormElement>
            <Datepicker
              errorMessage={t(errors?.endDate?.message as string)}
              inputLabel={`${t('executedOperations_endDate_label')} *`}
              onChange={handleDateChange('endDate')}
              value={watch('endDate') as Date}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t((errors?.product as FieldError)?.message as string)}
              items={products}
              label={t('executedOperations_product_label')}
              name="product"
              onChange={handleProductChange(false)}
              value={watch('product')}
            />
          </FormElement>
          <FormElement>
            <Combobox
              errorMessage={t((errors?.productOperation as FieldError)?.message as string)}
              items={productOperations}
              label={t('executedOperations_productOperation_label')}
              name="productOperation"
              onChange={handleProductOperationChange}
              value={watch('productOperation')}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <TextInput
              errorMessage={t(errors?.referenceNumber?.message as string)}
              formatter={formatAlphaNumeric}
              label={t('executedOperations_referenceNumber_label')}
              maxLength={20}
              name="referenceNumber"
              onChange={handleTextChange('referenceNumber')}
              value={watch('referenceNumber')}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="operationStatus"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.operationStatus as FieldError)?.message as string)}
                  items={productStatuses}
                  label={t('executedOperations_operationStatus_label')}
                  name="operationStatus"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="amountFrom"
              render={({ onChange, value }) => (
                <CurrencyInput
                  currencySymbol="₽"
                  errorMessage={t(errors?.amountFrom?.message as string)}
                  label={t('executedOperations_amountFrom_label')}
                  maxLength={20}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="amountTo"
              render={({ onChange, value }) => (
                <CurrencyInput
                  currencySymbol="₽"
                  errorMessage={t(errors?.amountTo?.message as string)}
                  label={t('executedOperations_amountTo_label')}
                  maxLength={20}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>

        {criteriaList?.length > 0 ? (
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="executedOperations_extraSearchCriteria_text" />
            </Subtitle>

            <FormGrid>
              {criteriaList
                .filter((criteria) => criteria.visible)
                .map((criteria) => {
                  const criteriaId = criteria.id.toLowerCase();
                  return (
                    <TextInput
                      key={`text-input-criteria-${criteriaId}`}
                      formatter={CriteriaFormatterMapping[criteria.inputType]}
                      label={criteria.label}
                      maxLength={criteria.maxLength || 20}
                      name={criteriaId}
                      onChange={handleTextChange(criteriaId)}
                      value={watch(criteriaId)}
                    />
                  );
                })}
            </FormGrid>
          </>
        ) : null}

        <Separator />
        <ButtonRow>
          <Button
            color={ButtonColor.Primary}
            disabled={isLoadingTable}
            size={ButtonSize.Medium}
            text={t('general_components_search_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
      {isShowResult ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="executedOperations_operationList_text" />
          </Subtitle>

          <TableWrapper>
            <Table
              emptyTableText={t('general_components_notFound_errorMessage')}
              hasNextPage={hasNextPage}
              headers={operationTableHeaders.map((head) => t(head))}
              items={operationTableItems}
              loading={isLoadingTable}
              loadPageItems={loadExecutedOperations}
              onSelect={handleSelectOperation}
              pageNumber={page}
              selectedItem={selectedOperation}
              tableWidth={TABLE_WIDTHS.DESKTOP_3X}
              widths={getColumnWidths(operationTableHeaders.length)}
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>

          {originalOperationData ? (
            <>
              <Separator />
              <FormRow>
                <Button
                  color={ButtonColor.Secondary}
                  onClick={handleViewStatus}
                  size={ButtonSize.Medium}
                  text={t('executedOperations_operationStatus_button')}
                  type="button"
                />

                {originalOperationData.isCancelButtonVisible ? (
                  <Button
                    color={ButtonColor.Primary}
                    disabled={isLoadingAction}
                    onClick={toggleModal('cancelConfirm')}
                    size={ButtonSize.Medium}
                    text={t('general_cancel_button')}
                    type="button"
                  />
                ) : null}

                {originalOperationData.isEditButtonVisible ? (
                  <Button
                    color={ButtonColor.Primary}
                    disabled={isLoadingAction}
                    onClick={handleEdit}
                    size={ButtonSize.Medium}
                    text={t('general_edit_button')}
                    type="button"
                  />
                ) : null}
              </FormRow>
            </>
          ) : null}
        </>
      ) : null}

      {productOperationStatusList ? (
        <FormModal onClose={handleOperationStatusModalClose} size={MODAL_VIEW.MIDDLE} zIndex={900}>
          <Separator />
          <Subtitle>
            <Trans i18nKey="executedOperations_operationStatusHistory_title" />
          </Subtitle>

          <Separator />
          <Table
            headers={operationStatusTableHeaders.map((head) => t(head))}
            items={productOperationStatusList}
            pageNumber={1}
            widths={['25', '20', '23', '32']}
          />
        </FormModal>
      ) : null}

      {showModal.cancelConfirm ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={<Markdown text={t('executedOperations_confirmCancel_text')} />}
          onCancel={toggleModal('cancelConfirm')}
          onSubmit={handleCancelSubmit}
          submitText={t('general_components_ok_button')}
          title={t('general_confirmationPopUp_title')}
          type={ModalType.Confirmation}
        />
      ) : null}

      {showModal.cancelledResult ? (
        <Modal
          description={t('executedOperations_cancel_successMessage')}
          onCancel={toggleModal('cancelledResult')}
          submitText={t('general_components_ok_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}

      {isLoadingAction ? <Loader /> : null}
    </Container>
  );
};

export default OperationSearch;
